import React, { useMemo } from 'react';
import { Icon } from '@popmenu/common-ui';
import { ArrowLeft, ArrowRight } from '@popmenu/web-icons';

import { useIntl } from 'react-intl';
import { useFormContext } from 'react-form';
import { useSelfSignUpContext } from './useSelfSignUpContext';
import type { SelfSignUpForm } from '../types';
import { getPlanSteps } from '../PlansStep';

const EndIcon = <Icon icon={ArrowRight} />;
const StartIcon = <Icon icon={ArrowLeft} />;

export const useMessages = () => {
  const intl = useIntl();

  return {
    back: intl.formatMessage({ defaultMessage: 'Back' }),
    congratulationsTitle: intl.formatMessage({ defaultMessage: 'Congratulations' }),
    goToCreateAccount: intl.formatMessage({ defaultMessage: 'Go to create account' }),
    goToMyPop: intl.formatMessage({ defaultMessage: 'Go to my pop' }),
    goToSummary: intl.formatMessage({ defaultMessage: 'Go to summary' }),
    goToWebsiteInfo: intl.formatMessage({ defaultMessage: 'Go to website info' }),
    next: intl.formatMessage({ defaultMessage: 'next' }),
    restaurantInfoTitle: intl.formatMessage({ defaultMessage: 'Your Restaurant info' }),
    signUpTitle: intl.formatMessage({ defaultMessage: 'Sign-up' }),
    siteInfoTitle: intl.formatMessage({ defaultMessage: 'Your Website Information' }),
    summaryTitle: intl.formatMessage({ defaultMessage: 'Summary' }),

  };
};

const useSelfSignUpFormSteps = () => {
  const { isNextButtonDisabled, isSignUpComplete, isSignUpErrored, isSignUpStarted } = useSelfSignUpContext();
  const messages = useMessages();
  const { values } = useFormContext<SelfSignUpForm>();
  const activeStepNames = useMemo(
    () => getPlanSteps(values.productPlan ?? 'starter').steps.map(s => s.name),
    [values.productPlan],
  );
  const nextStepAfterRestaurant = activeStepNames[1];

  const allSteps = useMemo(() => [
    {
      actionBar: {
        nextButtonProps: {
          'data-testid': 'sign-up-restaurant-info-next-button',
          'data-tour-id': 'sign-up-restaurant-info-next-button',
          disabled: isNextButtonDisabled,
          endIcon: EndIcon,
        },
        nextButtonText: nextStepAfterRestaurant === 'sign_up_step' ? messages.goToCreateAccount : messages.goToWebsiteInfo,
        previousButtonProps: { hidden: true },
      },
      name: 'restaurant_info_step',
      title: messages.restaurantInfoTitle,
    },
    {
      actionBar: {
        nextButtonProps: {
          'data-testid': 'sign-up-site-info-next-button',
          'data-tour-id': 'sign-up-site-info-next-button',
          disabled: isNextButtonDisabled,
          endIcon: EndIcon,
        },
        nextButtonText: messages.goToSummary,
        previousButtonProps: {
          'data-tour-id': 'sign-up-site-info-back-button',
          startIcon: StartIcon,
        },
        previousButtonText: messages.back,
      },
      name: 'site_info_step',
      title: messages.siteInfoTitle,
    },
    {
      actionBar: {
        nextButtonProps: {
          'data-tour-id': 'sign-up-summary-next-button',
          disabled: isNextButtonDisabled,
          endIcon: EndIcon,
        },
        nextButtonText: messages.goToCreateAccount,
        previousButtonProps: {
          'data-tour-id': 'sign-up-summary-back-button',
          startIcon: StartIcon,
        },
        previousButtonText: messages.back,
      },
      name: 'summary_step',
      title: messages.summaryTitle,
    },
    {
      actionBar: {
        cancelButtonProps: {
          hidden: true,
        },
        finishButtonText: messages.goToMyPop,
        nextButtonProps: {
          'data-testid': 'sign-up-next-button',
          'data-tour-id': 'sign-up-next-button',
          disabled: !isSignUpComplete,
          endIcon: EndIcon,
        },
        previousButtonProps: {
          'data-tour-id': 'sign-up-back-button',
          hidden: isSignUpComplete || (isSignUpStarted && !isSignUpErrored),
          startIcon: StartIcon,
        },
        previousButtonText: messages.back,
      },
      name: 'sign_up_step',
      title: messages.signUpTitle,
    },
  ], [isNextButtonDisabled, isSignUpComplete, isSignUpErrored, isSignUpStarted, nextStepAfterRestaurant, messages]);

  return useMemo(
    () => allSteps.filter(step => activeStepNames.includes(step.name as typeof activeStepNames[number])),
    [allSteps, activeStepNames],
  );
};

export default useSelfSignUpFormSteps;
